import React, { useMemo } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import { defaultPostOfficeContextValue, InternalPostOfficeContextProvider } from '../post-office-context';
import { defaultEnvironmentValues, PostOfficeEnvironmentProvider } from '../post-office-environment';
import { RouteListenerProvider } from '../post-office-route/listener';
import { useStableObject } from '../util/use-stable-object';
import { createMessagePlacementValues, MessagePlacementProvider } from './message-placement';
import { generateEnvironmentConfigWithOverrides } from './util/get-environment-with-overrides';

/**
 * A provider component for managing the context and configuration of all post office placements.
 * This should only appear once at a high level in your product
 *
 * @component
 * @param {Object} props - The properties passed to the PostOfficeProvider component.
 * @param {PostOfficeContextConfig} props.context - Configuration related to the current post office context,
 * including any relevant state or settings specific to the current session or user.
 * @param {PostOfficeEnvironmentConfig} props.environment - Configuration related to the environment
 * the application is currently running in. This includes the environment type (e.g., development, production)
 * and any optional remote module configuration or environment-specific overrides.
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 *
 * **Example Usage:**
 * ```jsx
 * <PostOfficeProvider
 *   context={{ current: { ... } }}
 *   environment={{
 *     env: "production",
 *     overrides: { ... }
 *   }}
 * >
 *   <YourApp />
 * </PostOfficeProvider>
 * ```
 */
export var PostOfficeProvider = function PostOfficeProvider(_ref) {
  var envWithOverrides = _ref.environment,
    context = _ref.context,
    children = _ref.children;
  var stableEnvironment = useStableObject(generateEnvironmentConfigWithOverrides(defaultEnvironmentValues, envWithOverrides));
  var stableContext = useStableObject(composeConfig(defaultPostOfficeContextValue, context.current));
  var memoizedMessagePlacementValues = useMemo(function () {
    return createMessagePlacementValues();
  }, []);
  return /*#__PURE__*/React.createElement(PostOfficeEnvironmentProvider, {
    value: stableEnvironment
  }, /*#__PURE__*/React.createElement(InternalPostOfficeContextProvider, {
    value: stableContext
  }, /*#__PURE__*/React.createElement(MessagePlacementProvider, {
    value: memoizedMessagePlacementValues
  }, /*#__PURE__*/React.createElement(RouteListenerProvider, null, children))));
};
var composeConfig = function composeConfig(base) {
  for (var _len = arguments.length, rest = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    rest[_key - 1] = arguments[_key];
  }
  return rest.reduce(function (a, v) {
    return merge(a, v);
  }, cloneDeep(base));
};